// 会員サイト用の日付/時刻フォーマッタ (日本時間 = Asia/Tokyo で統一)。
//
// サーバ側の時刻は 2 系統で保存される:
//   1. SQLite datetime('now')      → "2026-06-05 04:46:12" (UTC, タイムゾーン表記なし)
//   2. JS new Date().toISOString() → "2026-06-05T04:46:12.000Z" (UTC, Z 付き)
//
// 1 の形式を new Date() に渡すとブラウザのローカル時刻として誤解釈され 9 時間ズレる。
// parseServerDate() はタイムゾーン表記の有無を判定し、無ければ UTC として補正する。
// 表示は常に timeZone: "Asia/Tokyo" を明示し、閲覧者の端末設定に依存しない。

const JP_WEEKDAY = ["日", "月", "火", "水", "木", "金", "土"];

/**
 * サーバ保存の時刻文字列を Date に変換する。
 * - Z / +09:00 等のタイムゾーン表記があればそのまま解釈
 * - 無ければ (SQLite datetime 形式) UTC とみなし補正する
 * 不正値・null は null を返す。
 */
export function parseServerDate(
  v: string | null | undefined,
): Date | null {
  if (!v) return null;
  let s = String(v).trim();
  if (!s) return null;
  const hasTz = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  if (!hasTz) {
    // "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DDTHH:MM:SSZ"
    s = s.replace(" ", "T") + "Z";
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** 日時を「2026/6/5 13:46:12」形式 (JST) で返す。null は "-"。 */
export function fmtDateTime(v: string | null | undefined): string {
  const d = parseServerDate(v);
  if (!d) return "-";
  return d.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" });
}

/** 日付のみを「2026/6/5」形式 (JST) で返す。null は "-"。 */
export function fmtDate(v: string | null | undefined): string {
  const d = parseServerDate(v);
  if (!d) return "-";
  return d.toLocaleDateString("ja-JP", { timeZone: "Asia/Tokyo" });
}

/**
 * イベント開始日時を「4月20日 (土)」と「19:00」の 2 断片に分ける (JST)。
 * null / 不正値の場合は {date: "日時未定", time: ""}。
 */
export function formatEventDate(
  iso: string | null | undefined,
): { date: string; time: string } {
  const d = parseServerDate(iso);
  if (!d) return { date: "日時未定", time: "" };
  // Asia/Tokyo の各パートを取得 (端末タイムゾーン非依存)
  const parts = new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "numeric",
    day: "numeric",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const m = get("month");
  const day = get("day");
  // weekday は "土" のような形式 (ja-JP short)
  const w = get("weekday").replace(/曜日?$/, "");
  let hh = get("hour");
  const mm = get("minute");
  if (hh === "24") hh = "00"; // hour12:false の 0 時対策
  return { date: `${m}月${day}日 (${w})`, time: `${hh}:${mm}` };
}

/** publishedAt が直近 N 日以内 (NEW 判定) かどうか。タイムゾーン非依存。 */
export function isWithinDays(
  v: string | null | undefined,
  days: number,
): boolean {
  const d = parseServerDate(v);
  if (!d) return false;
  return Date.now() - d.getTime() < days * 24 * 3600 * 1000;
}
